import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { BillboardText, Card, FlowGrid } from '@fs/zion-ui'
import { getUser } from '@fs/zion-user'
import EventsCardHeader from './components/EventsCardHeader'
import EventsReviewList from './components/EventsReviewList'
import EventsCardEditMode from './EventsCardEditMode'
import { FullTextApp } from '../../../../shared/services/config'
import { getEventsFromPerson } from './utils/events.utils'
import { DISABLED_TEXT_COLOR } from '../shared/utils/panelConstants'

/**
 * EventsCard
 *
 * A card that displays a person's events. It shows a header, a review list
 * when not editing, and an edit mode when editing or when `editMode` prop is true.
 *
 * Props:
 * @param {Object} props.person - The SLS person record object containing existing events if any.
 * @param {Function} props.onSave - Callback fired when events are saved. Dispatches are also provided. (Required)
 * @param {boolean} [props.reviewed=false] - If true, marks the card as reviewed (affects styling).
 * @param {Function} [props.setReviewed] - Callback to change the reviewed state.
 * @param {boolean} [props.editMode=false] - If true, forces the card into edit mode externally. *
 * @param {Function} [props.setEditMode] - Setter for the edit mode state.
 * @param {boolean} [props.saveCardDataToSls=false] - Whether to save the card data to SLS upon submission.
 * @param {string} [props.appName=FullTextApp] - Name of the app context for the card.
 * @param {Function} [props.onDirtyChange=(isDirty) => {}] - Callback function invoked when the form dirty state changes.
 * @param {boolean} [props.showDataInPendingState=false] - Whether to show data when the card is in a pending state.
 *
 * Internal State:
 * @state {boolean} isEditing - Local state for toggling edit mode within the card.
 *
 * @returns {JSX.Element} Rendered EventsCard component.
 */
const EventsCard = ({
  person,
  onSave,
  reviewed = false,
  setReviewed,
  editMode = false,
  setEditMode,
  saveCardDataToSls = false,
  appName = FullTextApp,
  onDirtyChange = () => {},
  showDataInPendingState = false,
}) => {
  const [userId, setUserId] = useState(null)

  const events = useMemo(() => (person ? getEventsFromPerson(person) : []), [person])

  useEffect(() => {
    getUser().then((u) => setUserId(u.cisId))
  }, [])

  if (!onSave) {
    console.error('ReviewCard: onSave prop is required but was not provided')
    return null
  }

  return (
    <Card margins="xxs" edges={editMode === false ? 'border' : 'elevation'}>
      <BillboardText primaryColor={!reviewed && !editMode ? DISABLED_TEXT_COLOR : undefined}>
        <FlowGrid orientation="vertical" columnCount={1} gutters="xs">
          <EventsCardHeader setIsEditing={setEditMode} editMode={editMode} reviewed={reviewed} />
          {!editMode && (
            <EventsReviewList
              person={person}
              events={events}
              editMode={editMode}
              reviewed={reviewed}
              showDataInPendingState={showDataInPendingState}
            />
          )}
          {editMode && (
            <EventsCardEditMode
              person={person}
              events={events}
              reviewed={reviewed}
              setReviewed={setReviewed}
              setIsEditing={setEditMode}
              userId={userId}
              onSave={onSave}
              appName={appName}
              saveCardDataToSls={saveCardDataToSls}
              onDirtyChange={onDirtyChange}
            />
          )}
        </FlowGrid>
      </BillboardText>
    </Card>
  )
}

export default EventsCard

EventsCard.propTypes = {
  person: PropTypes.shape({}),
  onSave: PropTypes.func.isRequired,
  reviewed: PropTypes.bool,
  setReviewed: PropTypes.func,
  editMode: PropTypes.bool,
  setEditMode: PropTypes.func,
  appName: PropTypes.string,
  saveCardDataToSls: PropTypes.bool,
  onDirtyChange: PropTypes.func,
  showDataInPendingState: PropTypes.bool,
}

EventsCard.defaultProps = {
  person: null,
  setReviewed: undefined,
  setEditMode: undefined,
  reviewed: false,
  editMode: false,
  appName: FullTextApp,
  saveCardDataToSls: false,
  onDirtyChange: () => {},
  showDataInPendingState: false,
}
